"use client";

/**
 * MarketplaceTabsBar — the in-page Abilities/Skills/Agents tab switcher for
 * LOGGED-IN users (the app-sidebar shell). Anonymous visitors get these tabs
 * from the public MarketplaceShell's top bar instead, so this is rendered only
 * inside the authed branch of each marketplace route.
 *
 * Active tab is derived from the route (usePathname); tabs are Links to the
 * canonical paths so navigation is instant (prefetch) and accessible. Restored
 * from the segmented TabsBar that the route-split migration (#919) removed.
 */

import Link from "next/link";
import { usePathname } from "next/navigation";

const TABS = [
  { label: "Abilities", href: "/abilities" },
  { label: "Skills", href: "/skills" },
  { label: "Agents", href: "/agents" },
] as const;

export function MarketplaceTabsBar() {
  const pathname = usePathname() ?? "";
  return (
    <div className="flex items-center justify-between gap-4">
      <div
        className="inline-flex items-center gap-1 p-1 rounded-20 bg-bg-elevated border border-border-subtle shadow-marketplace-grid-inline"
        role="tablist"
        aria-label="Marketplace sections"
      >
        {TABS.map((tab) => {
          const isActive =
            pathname === tab.href || pathname.startsWith(tab.href + "/");
          return (
            <Link
              key={tab.href}
              href={tab.href}
              role="tab"
              aria-selected={isActive}
              aria-current={isActive ? "page" : undefined}
              className={[
                "inline-flex items-center gap-2.5 px-5 py-2 rounded-16 text-3.5 transition-all duration-200",
                isActive
                  ? "bg-fg-default text-bg-base font-semibold shadow-md"
                  : "text-fg-muted hover:text-fg-default hover:bg-bg-muted/60 font-medium",
              ].join(" ")}
            >
              {tab.label}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
